import { useState } from 'react'
import { Chip, DataTable, type Column } from '@w7/ui'

/** Market-API / CM catalog fetch payloads (may nest under `{ ok, result }`). */
export type CatalogFetchResult = {
  ok?: boolean
  message?: string
  sportId?: string
  alreadyEnabled?: number
  maxEventsOn?: number
  competitionIds?: string[]
  eventIds?: string[]
  processed?: string[]
  enabledEvents?: string[]
  enabledMarkets?: string[]
  upserted?: number
  competitionCount?: number
  bySport?: Array<{ eventTypeId: string; fetched: number; upserted: number }>
  upsertedRates?: number
  marketIds?: number
  books?: number
  errors?: Array<{ eventId?: string; eventTypeId?: string; error: string }>
}

export type CmFetchEnvelope = {
  ok: boolean
  sport?: string
  result?: CatalogFetchResult | null
}

type IdRow = { id: string; eventId: string }
type ErrorRow = { id: string; key: string; error: string }
type BySportRow = { id: string; eventTypeId: string; fetched: number; upserted: number }

const FETCH_KEYS = new Set([
  'processed',
  'errors',
  'message',
  'eventIds',
  'enabledEvents',
  'enabledMarkets',
  'upserted',
  'upsertedRates',
  'bySport',
  'competitionCount',
])

function isFetchPayload(value: unknown): value is CatalogFetchResult {
  if (!value || typeof value !== 'object') return false
  return Object.keys(value).some((k) => FETCH_KEYS.has(k))
}

export function asCatalogFetchResult(data: CmFetchEnvelope | null): CatalogFetchResult | null {
  if (!data) return null
  const r = data.result
  if (!r || typeof r !== 'object') return null
  const nested = (r as { result?: unknown }).result
  if (isFetchPayload(nested)) return nested
  return isFetchPayload(r) ? r : (r as CatalogFetchResult)
}

function eventRows(result: CatalogFetchResult | null): { label: string; rows: IdRow[] } {
  const processed = result?.processed ?? []
  if (processed.length) {
    return {
      label: 'Processed events',
      rows: processed.map((eventId, i) => ({ id: `p-${i}-${eventId}`, eventId })),
    }
  }
  const enabled = result?.enabledEvents ?? []
  if (enabled.length) {
    return {
      label: 'Enabled events',
      rows: enabled.map((eventId, i) => ({ id: `e-${i}-${eventId}`, eventId })),
    }
  }
  const discovered = result?.eventIds ?? []
  if (discovered.length) {
    return {
      label: 'Discovered events',
      rows: discovered.map((eventId, i) => ({ id: `d-${i}-${eventId}`, eventId })),
    }
  }
  return { label: 'Processed events', rows: [] }
}

const idCols: Column<IdRow>[] = [
  {
    id: 'n',
    header: '#',
    width: 48,
    numeric: true,
    cell: (_row, i) => i + 1,
  },
  {
    id: 'eventId',
    header: 'Event id',
    cell: (row) => <code className="cm-mono">{row.eventId}</code>,
    sortValue: (row) => row.eventId,
  },
]

const errorCols: Column<ErrorRow>[] = [
  {
    id: 'key',
    header: 'Id',
    cell: (row) => <code className="cm-mono">{row.key}</code>,
    sortValue: (row) => row.key,
  },
  {
    id: 'error',
    header: 'Error',
    cell: (row) => row.error,
  },
]

const bySportCols: Column<BySportRow>[] = [
  {
    id: 'eventTypeId',
    header: 'Sport id',
    cell: (row) => <code className="cm-mono">{row.eventTypeId}</code>,
    sortValue: (row) => row.eventTypeId,
  },
  {
    id: 'fetched',
    header: 'Fetched',
    numeric: true,
    cell: (row) => row.fetched,
    sortValue: (row) => row.fetched,
  },
  {
    id: 'upserted',
    header: 'Upserted',
    numeric: true,
    cell: (row) => row.upserted,
    sortValue: (row) => row.upserted,
  },
]

/**
 * Tabular fetch response — summary chips + processed/errors tables.
 * Raw JSON stays behind a toggle for debugging.
 */
export function FetchResultPanel({
  data,
  emptyLabel = 'Result appears here after a successful fetch.',
  'aria-label': ariaLabel = 'fetch-result',
}: {
  data: CmFetchEnvelope | null
  emptyLabel?: string
  'aria-label'?: string
}) {
  const [showRaw, setShowRaw] = useState(false)
  const result = asCatalogFetchResult(data)

  if (!data) {
    return <p className="cm-result-empty">{emptyLabel}</p>
  }

  const { label: eventsLabel, rows: eventIdRows } = eventRows(result)
  const errors = (result?.errors ?? []).map((e, i) => {
    const key = e.eventId ?? e.eventTypeId ?? '—'
    return { id: `err-${i}-${key}`, key, error: e.error }
  })
  const bySport = (result?.bySport ?? []).map((row, i) => ({
    id: `bs-${i}-${row.eventTypeId}`,
    ...row,
  }))
  const ok = Boolean(data.ok && (result?.ok !== false))
  const message = result?.message ?? (ok ? 'Done' : 'Completed with issues')
  const processedCount =
    (result?.processed?.length ?? 0) ||
    (result?.enabledEvents?.length ?? 0) ||
    (result?.eventIds?.length ?? 0)

  return (
    <div className="cm-result-panel" aria-label={ariaLabel}>
      <div className="cm-result-summary">
        <Chip tone={ok ? 'profit' : 'loss'} size="sm" icon={ok ? 'check' : 'info'}>
          {ok ? 'OK' : 'Issues'}
        </Chip>
        {data.sport ? (
          <Chip tone="neutral" size="sm">
            {data.sport}
          </Chip>
        ) : null}
        {result?.sportId ? (
          <Chip tone="neutral" size="sm" title="Vendor sport id">
            sport {result.sportId}
          </Chip>
        ) : null}
        {typeof result?.alreadyEnabled === 'number' && typeof result?.maxEventsOn === 'number' ? (
          <Chip tone="info" size="sm" title="Enabled capacity">
            enabled {result.alreadyEnabled}/{result.maxEventsOn}
          </Chip>
        ) : null}
        {processedCount > 0 ? (
          <Chip tone="brand" size="sm">
            events {processedCount}
          </Chip>
        ) : null}
        {typeof result?.upserted === 'number' ? (
          <Chip tone="brand" size="sm">
            upserted {result.upserted}
          </Chip>
        ) : null}
        {typeof result?.competitionCount === 'number' && result.competitionCount > 0 ? (
          <Chip tone="info" size="sm">
            competitions {result.competitionCount}
          </Chip>
        ) : null}
        {typeof result?.upsertedRates === 'number' ? (
          <Chip tone="brand" size="sm">
            rates {result.upsertedRates}
          </Chip>
        ) : null}
        {(result?.enabledMarkets?.length ?? 0) > 0 ? (
          <Chip tone="info" size="sm">
            markets {result!.enabledMarkets!.length}
          </Chip>
        ) : null}
        {errors.length ? (
          <Chip tone="loss" size="sm">
            errors {errors.length}
          </Chip>
        ) : null}
      </div>

      <p className="cm-result-message">{message}</p>

      {bySport.length > 0 ? (
        <div className="cm-result-section">
          <h3 className="cm-result-section-title">By sport</h3>
          <DataTable
            label="Competition fetch by sport"
            density="compact"
            rows={bySport}
            columns={bySportCols}
            rowKey={(row) => row.id}
            maxHeight={220}
          />
        </div>
      ) : null}

      <div className="cm-result-section">
        <h3 className="cm-result-section-title">{eventsLabel}</h3>
        <DataTable
          label="Event ids from fetch"
          density="compact"
          rows={eventIdRows}
          columns={idCols}
          rowKey={(row) => row.id}
          maxHeight={280}
          empty={
            <p className="cm-result-empty" style={{ minHeight: '4rem' }}>
              No events in this result.
            </p>
          }
        />
      </div>

      {errors.length > 0 ? (
        <div className="cm-result-section">
          <h3 className="cm-result-section-title">Errors</h3>
          <DataTable
            label="Fetch errors"
            density="compact"
            rows={errors}
            columns={errorCols}
            rowKey={(row) => row.id}
            maxHeight={220}
            rowTone={() => 'loss'}
          />
        </div>
      ) : null}

      <div className="cm-result-raw">
        <button
          type="button"
          className="cm-inline-link"
          onClick={() => setShowRaw((v) => !v)}
          aria-expanded={showRaw}
        >
          {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
        </button>
        {showRaw ? (
          <pre className="cm-result-pre" aria-label={`${ariaLabel}-raw`}>
            {JSON.stringify(data, null, 2)}
          </pre>
        ) : null}
      </div>
    </div>
  )
}
